import React, { useRef, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  UploadSimple, Eye, CheckCircle, XCircle, ClockCounterClockwise, Prohibit, ShieldCheck,
} from "@phosphor-icons/react";

export const STATUS_META = {
  pending_upload: { label: "Pending Upload", cls: "bg-gray-100 text-gray-600" },
  pending_verification: { label: "Pending Verification", cls: "bg-amber-100 text-amber-700" },
  verified: { label: "Verified", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  not_applicable: { label: "Not Applicable", cls: "bg-slate-100 text-slate-500" },
};

export const StatusPill = ({ status, testid }) => {
  const m = STATUS_META[status] || STATUS_META.pending_upload;
  return (
    <span className={`text-[10.5px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${m.cls}`} data-testid={testid}>
      {m.label}
    </span>
  );
};

export const openHrFile = async (docId) => {
  try {
    const { data, headers } = await api.get(`/hr/documents/${docId}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Could not open document"); }
};

const MAX = 5 * 1024 * 1024;

const DocRow = ({ eid, item, isAdmin, canVerify, onChanged }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const cur = item.current;
  const tid = item.key;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX) { toast.error("File must be 5 MB or smaller"); return; }
    if (cur?.status === "verified" && !window.confirm("This document is already verified. Replacing it resets verification. Continue?")) return;
    const fd = new FormData();
    fd.append("file", f);
    run(() => api.post(`/hr/employees/${eid}/documents/${item.key}`, fd), "Document uploaded — pending verification");
  };
  const verify = () => run(() => api.post(`/hr/documents/${cur.id}/verify`, { status: "verified" }), "Marked verified");
  const reject = () => {
    const reason = window.prompt("Rejection reason (what needs to be corrected?)");
    if (!reason?.trim()) return;
    run(() => api.post(`/hr/documents/${cur.id}/verify`, { status: "rejected", reason }), "Rejected — corrected document requested");
  };
  const markNa = () => {
    const reason = window.prompt("Why is this document not applicable?");
    if (!reason?.trim()) return;
    run(() => api.post(`/hr/employees/${eid}/not-applicable/${item.key}`, { reason }), "Marked not applicable");
  };
  const clearNa = () => run(() => api.delete(`/hr/employees/${eid}/not-applicable/${item.key}`), "Marked applicable again");
  const except = () => {
    const reason = window.prompt(`Admin exception for "${item.label}" — reason (saved in audit log)`);
    if (!reason?.trim()) return;
    run(() => api.post(`/hr/employees/${eid}/exceptions`, { item_key: item.key, reason }), "Exception recorded");
  };

  return (
    <>
      <tr data-testid={`hr-doc-row-${tid}`}>
        <td>
          <div className="font-medium text-[13px]">{item.label}</div>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {item.mandatory ? "Mandatory" : "Optional"}{item.excepted && " · Admin exception"}
          </div>
        </td>
        <td><StatusPill status={item.status} testid={`hr-doc-status-${tid}`} /></td>
        <td className="text-[11.5px] text-muted-foreground">
          {cur ? (<>v{cur.version} · {cur.filename}<br />Uploaded {fmtDate(cur.uploaded_at)} by {cur.uploaded_by}</>) : item.na ? `N/A: ${item.na.reason}` : "—"}
        </td>
        <td className="text-[11.5px] text-muted-foreground">
          {cur?.verified_at ? <>{fmtDate(cur.verified_at)} · {cur.verified_by}</> : "—"}
          {cur?.status === "rejected" && <div className="text-red-600" data-testid={`hr-doc-reason-${tid}`}>Reason: {cur.rejection_reason}</div>}
        </td>
        <td>
          <div className="flex items-center gap-1 justify-end flex-wrap">
            <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={upload} data-testid={`hr-doc-input-${tid}`} />
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy} onClick={() => ref.current?.click()} data-testid={`hr-doc-upload-${tid}`}>
              <UploadSimple size={12} className="mr-1" />{cur ? "Replace" : "Upload"}
            </Button>
            {cur && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View" onClick={() => openHrFile(cur.id).then(onChanged)} data-testid={`hr-doc-view-${tid}`}><Eye size={14} /></Button>
            )}
            {canVerify && cur && cur.status !== "verified" && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-700" title="Verify" disabled={busy} onClick={verify} data-testid={`hr-doc-verify-${tid}`}><CheckCircle size={15} /></Button>
            )}
            {canVerify && cur && cur.status !== "rejected" && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" title="Reject" disabled={busy} onClick={reject} data-testid={`hr-doc-reject-${tid}`}><XCircle size={15} /></Button>
            )}
            {!item.mandatory && !cur && (item.na
              ? <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={clearNa} data-testid={`hr-doc-na-clear-${tid}`}>Applicable</Button>
              : <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Not applicable" onClick={markNa} data-testid={`hr-doc-na-${tid}`}><Prohibit size={14} /></Button>)}
            {isAdmin && item.mandatory && item.status !== "verified" && !item.excepted && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-700" title="Admin exception" onClick={except} data-testid={`hr-doc-except-${tid}`}><ShieldCheck size={14} /></Button>
            )}
            {item.history.length > 0 && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Version history" onClick={() => setShowHist(!showHist)} data-testid={`hr-doc-hist-${tid}`}><ClockCounterClockwise size={14} /></Button>
            )}
          </div>
        </td>
      </tr>
      {showHist && item.history.map((h) => (
        <tr key={h.id} className="bg-muted/30 text-[11px]" data-testid={`hr-doc-version-${h.id}`}>
          <td className="pl-6">v{h.version}{h.is_current ? " (current)" : ""}</td>
          <td><StatusPill status={h.status} /></td>
          <td>{h.filename} · {fmtDate(h.uploaded_at)} · {h.uploaded_by}</td>
          <td>{h.verified_at ? `${fmtDate(h.verified_at)} · ${h.verified_by}` : "—"}{h.rejection_reason && ` · ${h.rejection_reason}`}</td>
          <td className="text-right">
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => openHrFile(h.id)}>View</Button>
            {isAdmin && (
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-destructive" data-testid={`hr-doc-version-delete-${h.id}`}
                onClick={() => window.confirm(`Delete v${h.version} of ${item.label}?`) && run(() => api.delete(`/hr/documents/${h.id}`), "Document version deleted")}>Delete</Button>
            )}
          </td>
        </tr>
      ))}
    </>
  );
};

export const DocChecklist = ({ eid, checklist, isAdmin, canVerify, onChanged }) => (
  <div className="card-flat overflow-x-auto">
    <table className="data-table" data-testid="hr-doc-checklist">
      <thead>
        <tr><th>Document</th><th>Status</th><th>Upload</th><th>Verification</th><th className="num"></th></tr>
      </thead>
      <tbody>
        {checklist.map((it) => <DocRow key={it.key} eid={eid} item={it} isAdmin={isAdmin} canVerify={canVerify} onChanged={onChanged} />)}
      </tbody>
    </table>
  </div>
);
